import DeliveryEvent from '@/models/deliveryEvent';
import { DELIVERY_EVENTS, UNAVAILABLE_CODE_DELIVERY_EVENT_MAP } from '@/constants/delivery-events';
import { DAYS_OF_WEEK, WEEKEND_DAYS } from '@/constants/date-time';

const REPORTABLE_CUTOFF_TYPES = ['zip-code', 'product'];

export default class DeliveryEventsReporter {
  constructor(availability, zipCode, sku) {
    this.availability = availability;
    this.zipCode = zipCode;
    this.sku = sku;

    this.hasReportableCutoffType = REPORTABLE_CUTOFF_TYPES.includes(availability.destinationFloristCutoffSource);
    this.hasReportableCutoff = !!availability.todaysDeliveryDate()
      && !!availability.destinationFloristCutoff
      && this.hasReportableCutoffType;

    this.hasReportableUnavailableSundays = this.availability.hasUnavailableSundays();
    this.hasReportableUnavailableSaturdays = this.availability.hasUnavailableSaturdays()
      && this.availability.isFloristShipping();

    this.hasReportableSameDayFeeFromZipCode = this.availability.hasSameDayFeeFromZipCode();
  }

  /**
   * The delivery events to fire based on the returned availability from the API
   *
   * @returns Array
   */
  events() {
    if (!this.availability.isAvailable) {
      return [this._unavailableEvent()];
    }

    let events = [];

    if (this.hasReportableCutoff) {
      events.push(this._cutoffEvent());
    }

    if (this.hasReportableUnavailableSundays) {
      events.push(this._sundayUnavailableEvent());
    }

    if (this.hasReportableUnavailableSaturdays) {
      events.push(this._saturdayUnavailableEvent());
    }

    if (this.hasReportableSameDayFeeFromZipCode) {
      events.push(this._sameDayFeeFromZipCodeEvent());
    }

    return events;
  }


  // "private" methods

  /**
   * An event describing that the delivery method's cutoff was overridden
   * by a the zip code's cutoff or the product cutoff offset
   *
   * @returns DeliveryEvent
   */
  _cutoffEvent() {
    const dayOfWeekForToday = this.availability.todaysDeliveryDate().dayOfWeek;
    const cutoffHour = this.availability.destinationFloristCutoffHour().toString();

    const type = this.availability.destinationFloristCutoffSource.replace('-', '_').toUpperCase();
    const timeFrame = WEEKEND_DAYS.includes(dayOfWeekForToday) ? dayOfWeekForToday.toUpperCase() : 'WEEKDAY';
    const name = DELIVERY_EVENTS.SAME_DAY_CUTOFF_PRIORITY[type][timeFrame];

    return new DeliveryEvent(name, cutoffHour, this.zipCode);
  }

  /**
   * An event describing that there is at least one occurence of unavailability
   * for Sunday and the current shippingType
   *
   * @returns DeliveryEvent
   */
  _sundayUnavailableEvent() {
    return this._dayOfWeekUnavailableEvent(DAYS_OF_WEEK.SUNDAY);
  }

  /**
   * An event describing that there is at least one occurence of unavailability
   * for Saturday and the current shippingType
   *
   * @returns DeliveryEvent
   */
  _saturdayUnavailableEvent() {
    return this._dayOfWeekUnavailableEvent(DAYS_OF_WEEK.SATURDAY);
  }

  /**
   * An event describing that there is at least one occurence of unavailability
   * for the given day of week
   *
   * @returns DeliveryEvent
   */
  _dayOfWeekUnavailableEvent(dayOfWeek) {
    const shippingType = this.availability.shippingType().toUpperCase();
    const day = dayOfWeek.toUpperCase();
    const name = DELIVERY_EVENTS.DAY_OF_WEEK_UNAVAILABLE[shippingType][day];

    return new DeliveryEvent(name, this.zipCode, this.sku);
  }

  /**
   * An event describing that the API-returned same day price is sourced
   * from the zip code
   *
   * @returns DeliveryEvent
   */
  _sameDayFeeFromZipCodeEvent() {
    return this._sameDayDeliveryFeeEvent('ZIP_CODE');
  }

  /**
   * An event describing that the API-returned same day price is sourced
   * from something other than the delivery method
   *
   * As of 2021-08-13, note that zip code is _currently_ the only non-delivery method
   * same day fee that we report and that we do not factor in if the session is
   * fee exempt. We report even is the user doesn't see the price.
   *
   * @returns DeliveryEvent
   */
  _sameDayDeliveryFeeEvent(type) {
    const name = DELIVERY_EVENTS.SAME_DAY_DELIVERY_FEE[type];
    const price = this.availability.firstPossibleDeliveryDate().price.toString();

    return new DeliveryEvent(name, price, this.zipCode);
  }

  /**
   * The event that describes why the user is seeing no availability
   *
   * @returns DeliveryEvent
   */
  _unavailableEvent() {
    const name = UNAVAILABLE_CODE_DELIVERY_EVENT_MAP[this.availability.unavailableCode] ?? this.availability.unavailableCode;

    return new DeliveryEvent(name, this.zipCode, this.sku);
  }
}